import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Link,
  Skeleton,
  Stack,
  Text,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import { CreditCard } from '@olo-web/assets/icons/payments/CreditCard.ui';
import { CreditCardCVV } from '@olo-web/assets/icons/payments/CreditCardCVV.ui';
import {
  useCheckoutDispatch,
  useCheckoutState,
  useContactInfoState,
  useCustomerState,
  useScreenSizeState,
} from '@olo-web/client-state';
import { useAnnouncer } from '@olo-web/client-state/AnnouncerContext';
import { AnimateHeight } from '@olo-web/components/atoms/AnimateHeight';
import { IndicatesRequired } from '@olo-web/components/atoms/IndicatesRequired';
import { HookFormInput } from '@olo-web/components/molecules/HookFormInput/HookFormInput.ui';
import { ELegalLinks, EValidationFields } from '@olo-web/types/enums';
import {
  useIsDineIn,
  useIsLevelup,
  useIsScanAndPayPage,
  useIsStartATabModal,
} from '@olo-web/utils/common/hooks';
import { caPostalCodeRegex, usPostalCodeRegex } from '@olo-web/utils/common/validationSchema';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useId, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import * as yup from 'yup';
import { CollectJS } from '../../common/components/CollectJS';
import { SignInButton } from '../../SignInButton';
import { VerifyCreditCardButton } from '../../VerifyCreditCardButton';
import { CardDetailsDisplay } from '../CardDetailsDisplay';
import { CheckoutFormInputStyles } from './CheckoutFormInputStyles';

const CJS_IFRAME_TITLES: Record<string, string> = {
  'cjs-ccnumber': 'Card Number',
  'cjs-ccexp': 'Card Expiration',
  'cjs-cvv': 'CVV Code',
};

const InputSkeleton = () => (
  <Skeleton pos="absolute" w="100%" mb={2} height="44px" borderRadius="8px" />
);

const CollectJsErrorLabel = ({ id, message }: { id: string; message: string }) => (
  <Box
    textStyle="label"
    color="danger.400"
    mt={2}
    data-testid={id}
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    id={id}
  >
    {message}
  </Box>
);

const SaveCardCheckbox = () => {
  const { customerId } = useCustomerState();
  const { saveCard } = useCheckoutState();
  const checkoutDispatch = useCheckoutDispatch();

  const handleSaveCardChange = useCallback(() => {
    checkoutDispatch({
      type: 'SET_SAVE_CARD_AT_CHECKOUT',
      payload: !saveCard,
    });
  }, [saveCard, checkoutDispatch]);

  if (!customerId) return null;

  return (
    <Box>
      <Checkbox
        type="checkbox"
        isChecked={saveCard}
        spacing={2}
        iconSize="1rem"
        colorScheme="primary"
        onChange={handleSaveCardChange}
        data-testid="save-card-checkbox"
        aria-label="Save card for next time"
      >
        <Text aria-hidden="true">Save card for next time</Text>
      </Checkbox>
      <Text textStyle="label" color="blackAlpha.400">
        By saving your payment method, you agree to the{' '}
        <Link
          isExternal
          href={ELegalLinks.TERMS_OF_USE}
          textDecor="underline"
          aria-label="Terms of Use (opens in new window)"
        >
          Terms of Use
        </Link>
        .
      </Text>
    </Box>
  );
};
const validatePostalCode = (value) => {
  const str = String(value);
  if (str.match(new RegExp(usPostalCodeRegex)) || str.match(new RegExp(caPostalCodeRegex)))
    return null;
  return 'Please enter a valid ZIP/postal code in the United States or Canada';
};

const validateEmail = async (value) => {
  const str = String(value);
  const schema = yup.string().email('Email is not formatted properly');

  try {
    await schema.validate(str);
    return null;
  } catch (e) {
    return e.errors[0];
  }
};

export function CardDetailsForm({
  showEmailField,
  showSignInButton = true,
}: {
  showEmailField: boolean;
  showSignInButton?: boolean;
}): JSX.Element {
  const { register, formState, errors, setValue, clearErrors } = useFormContext();
  const {
    ccFieldsAvailable,
    invalidCollectJsInputFieldNames: invalid,
    addedPayment,
    selectedCard,
  } = useCheckoutState();
  const { isMdDown } = useScreenSizeState();
  const checkoutDispatch = useCheckoutDispatch();
  const isScanAndPayPage = useIsScanAndPayPage();
  const isLevelup = useIsLevelup();
  const isDineIn = useIsDineIn();
  const contactInfo = useContactInfoState();
  const isStartATabModal = useIsStartATabModal();
  const collectJsFieldIds = useId();
  const { announce } = useAnnouncer();
  const prevInvalidRef = useRef<EValidationFields[]>([]);

  const handleRemoveAddedCard = () => {
    checkoutDispatch({
      type: 'REMOVE_PAYMENT',
    });
  };

  useUpdateEffect(() => {
    if (addedPayment === null) {
      setValue('payments[0].creditCardInfo.zipCode', '');
    }
  }, [addedPayment]);

  useUpdateEffect(() => {
    if (selectedCard === null) {
      clearErrors();
    }
  }, [selectedCard]);

  // CollectJS inputs live in cross-domain iframes, so validation state only reaches React via
  // `invalid`. Route each transition into the invalid set through the global announcer so the
  // screen reader reliably reads the error without our code having to steal focus.
  useUpdateEffect(() => {
    const current = invalid ?? [];
    const prev = prevInvalidRef.current;
    const newlyInvalid = current.filter((field) => !prev.includes(field));
    const messages: string[] = [];
    if (newlyInvalid.includes(EValidationFields.CC_NUMBER)) {
      messages.push('Error: please enter a valid card number.');
    }
    if (newlyInvalid.includes(EValidationFields.CC_EXPIRY)) {
      messages.push('Error: please enter a valid expiration date.');
    }
    if (newlyInvalid.includes(EValidationFields.CVV)) {
      messages.push('Error: please enter a valid CVV/CVC number.');
    }
    if (messages.length > 0) {
      announce(messages.join(' '));
    }
    prevInvalidRef.current = current;
  }, [invalid]);

  const ccNumberErrorMessage = invalid?.includes(EValidationFields.CC_NUMBER)
    ? 'Error: please enter a valid card number'
    : '';
  const ccExpiryErrorMessage = invalid?.includes(EValidationFields.CC_EXPIRY)
    ? 'Error: please enter a valid expiration date'
    : '';
  const cvvErrorMessage = invalid?.includes(EValidationFields.CVV)
    ? 'Error: please enter a valid CVV/CVC number'
    : '';
  useEffect(() => {
    if (!ccFieldsAvailable) return;
    Object.entries(CJS_IFRAME_TITLES).forEach(([containerId, title]) => {
      const iframe = document.getElementById(containerId)?.querySelector('iframe');
      if (iframe) iframe.setAttribute('title', title);
    });
  }, [ccFieldsAvailable, addedPayment]);

  const shouldShowSaveCard = !isLevelup && !isStartATabModal;
  const ccNumberLabelId = `${collectJsFieldIds}-ccnumber`;
  const ccExpLabelId = `${collectJsFieldIds}-ccexp`;
  const ccCvvLabelId = `${collectJsFieldIds}-cvv`;

  return (
    <VStack align="stretch" sx={CheckoutFormInputStyles}>
      {isMdDown && !isDineIn && (
        <Text as="h2" textStyle="h3" mb={2} data-testid="card-details-header">
          Card details
        </Text>
      )}
      <AnimatePresence>
        {addedPayment?.card ? (
          <AnimateHeight key="verified-credit-card">
            <VisuallyHidden role="alert" aria-live="assertive">
              Credit card added
            </VisuallyHidden>
            <VStack w="100%" alignItems="flex-start">
              <Flex
                align="flex-start"
                justify={{ base: 'space-between', md: 'flex-start' }}
                w="100%"
                mb={contactInfo?.email || isStartATabModal ? 2 : -4}
              >
                <CardDetailsDisplay
                  card={addedPayment.card}
                  accessibilityProps={{ role: 'status', 'aria-live': 'polite' }}
                />
                <Button
                  variant="tertiary"
                  px={0}
                  mx={{ base: 0, md: 8 }}
                  fontWeight="200"
                  colorScheme="primary"
                  textDecor="underline"
                  onClick={handleRemoveAddedCard}
                  alignItems={'flex-start'}
                  aria-label="Remove added credit card"
                >
                  Remove
                </Button>
              </Flex>
              {shouldShowSaveCard && <SaveCardCheckbox />}
            </VStack>
          </AnimateHeight>
        ) : (
          <AnimateHeight key="credit-card-details-form">
            {showSignInButton && ( //use showSignInButton instead
              <SignInButton>Sign in to view your saved cards</SignInButton>
            )}
            <IndicatesRequired mb={4} />
            <FormControl pos="relative" isRequired>
              <FormLabel id={ccNumberLabelId}>
                Card number
                {ccNumberErrorMessage && <VisuallyHidden>, {ccNumberErrorMessage}</VisuallyHidden>}
              </FormLabel>
              {!ccFieldsAvailable && <InputSkeleton />}
              <div
                id="cjs-ccnumber"
                data-testid="cjs-ccnumber"
                role="group"
                aria-labelledby={ccNumberLabelId}
                aria-describedby={ccNumberErrorMessage ? 'credit-card-error' : undefined}
                style={{ height: '44px' }}
              />
              {ccNumberErrorMessage && (
                <CollectJsErrorLabel id="credit-card-error" message={ccNumberErrorMessage} />
              )}
              <CreditCard pos="absolute" top={8} bottom={0} right={2} h={8} w={8} />
            </FormControl>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={4}
              w="100%"
              align="flex-start"
              justify="space-between"
              pb={4}
              pt={2}
            >
              <HStack w="100%" align="flex-start" spacing={4} justify="space-between">
                <FormControl pos="relative" isRequired>
                  <FormLabel id={ccExpLabelId}>
                    Expiration date
                    {ccExpiryErrorMessage && (
                      <VisuallyHidden>, {ccExpiryErrorMessage}</VisuallyHidden>
                    )}
                  </FormLabel>
                  {!ccFieldsAvailable && <InputSkeleton />}
                  <div
                    id="cjs-ccexp"
                    data-testid="cjs-ccexp"
                    role="group"
                    aria-labelledby={ccExpLabelId}
                    aria-describedby={ccExpiryErrorMessage ? 'expiration-date-error' : undefined}
                    style={{ height: '44px' }}
                  />
                  {ccExpiryErrorMessage && (
                    <CollectJsErrorLabel
                      id="expiration-date-error"
                      message={ccExpiryErrorMessage}
                    />
                  )}
                </FormControl>
                <FormControl pos="relative" isRequired>
                  <FormLabel id={ccCvvLabelId}>
                    CVV/CVC
                    {cvvErrorMessage && <VisuallyHidden>, {cvvErrorMessage}</VisuallyHidden>}
                  </FormLabel>
                  {!ccFieldsAvailable && <InputSkeleton />}
                  <div
                    id="cjs-cvv"
                    data-testid="cjs-cvv"
                    role="group"
                    aria-labelledby={ccCvvLabelId}
                    aria-describedby={cvvErrorMessage ? 'cvv-error' : undefined}
                    style={{ height: '44px' }}
                  />
                  {cvvErrorMessage && (
                    <CollectJsErrorLabel id="cvv-error" message={cvvErrorMessage} />
                  )}
                  <CreditCardCVV pos="absolute" top={8} bottom={0} right={2} h={8} w={8} />
                </FormControl>
              </HStack>
              <HookFormInput
                isRequired
                type="number"
                formState={formState}
                label="Billing ZIP code"
                autoComplete="zipCode"
                errors={errors}
                name="payments[0].creditCardInfo.zipCode"
                ref={register({
                  required: 'Error: please enter your Billing ZIP code',
                  validate: validatePostalCode,
                })}
                data-testid="zip-code-input"
                aria-label="Billing Zip code"
              />
            </Stack>
            {showEmailField && (
              <HookFormInput
                type="email"
                formState={formState}
                label="Email (optional, for receipt)"
                labelTestId="email-input-label"
                errors={errors}
                name="email"
                autoComplete="email"
                ref={register({ validate: validateEmail })}
                data-testid="email-input"
              />
            )}
            <VStack spacing={6} pt={2} w="100%" alignItems="flex-start">
              {shouldShowSaveCard && <SaveCardCheckbox />}
              {!(isScanAndPayPage || (isDineIn && !isStartATabModal)) && (
                <VerifyCreditCardButton>Continue</VerifyCreditCardButton>
              )}
            </VStack>
          </AnimateHeight>
        )}
      </AnimatePresence>
      <CollectJS />
    </VStack>
  );
}
